// Available Endorsements Response (POST applications/availableEndorsements)
// {
//   "availableEndorsements": {
//      "hiredAutoAndNonOwnedAuto": "{\"hiredCoverage\": \"hiredAutoLiability\",\"nonOwnedCoverage\": \"nonOwnedAutoLiabilityWithoutDeliveryService\"}",
//   }
// }
//
// Application Update Payload (POST applications)
// {
//   "endorsements": {
//      hiredAutoAndNonOwnedAuto: {
//      hiredCoverage: 1,
//      nonOwnedCoverage: 1,
//      autoLiabLimit: 500000,
//      limit: 500000,
//      shouldInclude: true
//    },
//   }
// }

// 1) selecting HNOA should expand and display sub questions
// 2) Caret can toggle HNOA open or closed
// 3) if user answers "Yes" to any of the sub questions, HNOA should be invalidated and deselected
// 4) if user answers "No" to all of the sub questions, HNOA should be selected and sub questions should collapse

import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {
  setHiredAndNonOwnedAuto,
  setUserHasInvalidatedAddon,
  setCheckedEndorsement,
} from '../../../../actions/application';
import {
  selectHiredCoverage,
  selectUserHasInvalidatedAddOns,
  selectCheckedEndorsements,
} from '../../../../selectors/application';
import { selectLoadingState } from '../../../../selectors';
import YesNoQuestion from '../../../Shared/YesNoQuestion';
import CenteredLoadingSpinner from '../../../AddOns/shared/CenteredLoadingSpinner';
import EndorsementCard, { EndorsementCardHandle } from '../../EndorsementCard';
import { SubQuestionCont } from '../../styles/endorsements';
import { config } from './HiredAuto.config';

const { endorsementKey: ENDORSEMENT_KEY, label: LABEL, description: DESCRIPTION } = config;

const VALIDATION_QUESTIONS = [
  'Does your business own any autos?',
  'Does your business ever use autos to transport passengers?',
  'Does your business ever operate your customer’s autos?',
];

const HiredAuto = () => {
  const dispatch = useDispatch();
  const invalidatedAddons = useSelector(selectUserHasInvalidatedAddOns) || [];
  const expandedRef = useRef<EndorsementCardHandle>(null);
  const isInvalid = invalidatedAddons.includes(ENDORSEMENT_KEY);
  const hasAddOnBeenAdded = useSelector(selectHiredCoverage);
  const isSelected = useSelector(selectCheckedEndorsements).includes(ENDORSEMENT_KEY);
  const isQuoteLoading = useSelector(selectLoadingState);
  const [answers, setAnswers] = useState<{ [key: number]: boolean }>({});
  const isDesktop = useMediaQuery({ minWidth: 768 });

  useEffect(() => {
    const areSubQuestionsComplete = Object.values(answers).length === VALIDATION_QUESTIONS.length;

    if (!areSubQuestionsComplete && isSelected && !hasAddOnBeenAdded) {
      expandedRef.current?.setExpanded(true);
    }

    if (areSubQuestionsComplete && !isInvalid && !hasAddOnBeenAdded) {
      // user must select "No" for all sub-questions to add the endorsement, otherwise the endorsement will be invalidated
      const isValid = !Object.values(answers).includes(true);
      expandedRef.current?.setExpanded(false);
      if (isValid) {
        dispatch(setHiredAndNonOwnedAuto(true));
        setAnswers({});
      } else {
        dispatch(setHiredAndNonOwnedAuto(false));
        dispatch(setCheckedEndorsement(ENDORSEMENT_KEY, false));
        dispatch(setUserHasInvalidatedAddon(ENDORSEMENT_KEY));
      }
    }
  }, [answers, isSelected, isInvalid, hasAddOnBeenAdded, dispatch]);

  const handleCheckboxChange = (checked: boolean) => {
    dispatch(setCheckedEndorsement(ENDORSEMENT_KEY, checked));
    if (!checked && hasAddOnBeenAdded) {
      dispatch(setHiredAndNonOwnedAuto(null));
    }
  };

  const handleYes = (index: number) => {
    setAnswers({ ...answers, [index]: true });
  };

  const handleNo = (index: number) => {
    setAnswers({ ...answers, [index]: false });
  };

  return (
    <EndorsementCard
      endorsementKey={ENDORSEMENT_KEY}
      label={LABEL}
      description={DESCRIPTION}
      isSelected={isSelected}
      onCheckboxChange={handleCheckboxChange}
      hasExpandableContent={!hasAddOnBeenAdded}
      hideCaret
      expandedRef={expandedRef}
    >
      {VALIDATION_QUESTIONS.map((question, index) => (
        <SubQuestionCont key={index} isActive={!isQuoteLoading} direction="row">
          <YesNoQuestion
            question={question}
            value={answers[index]}
            selectYes={() => handleYes(index)}
            selectNo={() => handleNo(index)}
            cyLabel={`${ENDORSEMENT_KEY}-addOn-validation-question-${question}`}
            dataTestId="endorsement-validation-question"
            small={isDesktop}
            backgroundColor="#fff"
          />
        </SubQuestionCont>
      ))}
      {isQuoteLoading && <CenteredLoadingSpinner useAltColor position="relative" />}
    </EndorsementCard>
  );
};

export default HiredAuto;
